import { Autocomplete, Button, Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import dayjs from "dayjs";
import { titleize } from "inflection";
import { filter, uniq } from "lodash";
import { map as fpMap } from "lodash/fp";
import React, { useContext, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import TimeAutocomplete from "@/components/TimeAutocomplete";
import { ReportContext } from "@/Contexts/ReportContext";
import { Issue, GetIssueCategoriesDocument, UpdateReportIssueDocument, GetReportIssuesDocument, IssueTypeEnum } from "@/graphql/generated/graphql";
import { applyHhMmToDay, getEndTimeOptionsAfterStart, isSameDayTimeRangeInverted, minutesOfDayFromDateTime } from "@/utils/dateTimeHelpers";
import { isRichTextVisuallyEmpty } from "@/utils/helperFunctions";
import TextEditor from "@/TextEditor";

const mapId = fpMap("id");

interface DiarySingleEntryProps {
  selectedDiaryEntry: Issue;
  setSelectedDiaryEntry: React.Dispatch<React.SetStateAction<Issue | null>>;
}

type DiarySingleEntryOnChangeProps = 
  | [field: "flagged", value: boolean]
  | [field: "headline" | "startTime" | "endTime" | "issueType" | "description", value: string];


const DiarySingleEntry: React.FC<DiarySingleEntryProps> = ({ selectedDiaryEntry, setSelectedDiaryEntry }) => {
  const { selectedReport } = useContext(ReportContext);
  const { data } = useQuery(GetIssueCategoriesDocument, { variables: { dynamic: false }, });
  const issueCategories = data?.getIssueCategories;
  const [diaryEntry, setDiaryEntry] = useState<Issue>(() => ({ ...selectedDiaryEntry }));
  const [diaryTags, setDiaryTags] = useState<Array<string>>(mapId(selectedDiaryEntry.categories || []));

  const [updateIssue] = useMutation(UpdateReportIssueDocument, {
    refetchQueries: [
      {
        query: GetReportIssuesDocument,
        variables: {
          reportId: selectedReport?.id as string,
        },
      },
    ],
  });

  const handleOnChange = (...[field, value]: DiarySingleEntryOnChangeProps) => {
    if (field === "startTime" || field === "endTime") {
      if (!value) {
        setDiaryEntry((prev) => ({
          ...prev,
          [field]: undefined,
        } as Issue));
        return;
      }

      const baseDay = dayjs();
      const selectedDateTime = applyHhMmToDay(baseDay, value);
      if (!selectedDateTime) return;

      const newMinutes = minutesOfDayFromDateTime(selectedDateTime);

      setDiaryEntry((prev) => {
        if (field === "startTime") {
          const newStartIso = selectedDateTime.toISOString();
          if (
            prev.endTime &&
            newMinutes >= minutesOfDayFromDateTime(prev.endTime)
          ) {
            return {
              ...prev,
              startTime: newStartIso,
              endTime: "",
            }
          }
          return { ...prev, startTime: newStartIso };
        }

        return {
          ...prev,
          endTime: selectedDateTime.toISOString(),
        };
      });
      return;
    }

    setDiaryEntry((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateEntry = () => {
    updateIssue({
      variables: {
        reportIssue: {
          id: diaryEntry?.id as string,
          headline: diaryEntry?.headline as string,
          startTime: diaryEntry?.startTime ?? null,
          endTime: diaryEntry?.endTime ?? null,
          issueType: diaryEntry?.issueType as IssueTypeEnum,
          description: diaryEntry?.description as string,
          flagged: diaryEntry?.flagged as boolean,
          ...(diaryTags && { categoryIds: diaryTags }),
        }
      },
      onCompleted: () => {
        setSelectedDiaryEntry(null);
      }
    })
  }

  const endTimeOptions = getEndTimeOptionsAfterStart(diaryEntry.startTime);

  const isRangeInverted = isSameDayTimeRangeInverted(
    diaryEntry.startTime,
    diaryEntry.endTime
  );

  const disableButton =
    isRangeInverted || isRichTextVisuallyEmpty(diaryEntry.description);

  return (
    <React.Fragment>
      {
        selectedDiaryEntry && (
          <Grid container size={12} direction="column" spacing={1} pt={2} key={selectedDiaryEntry.id}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Headline"
                value={diaryEntry.headline || ""}
                onChange={(event) => handleOnChange("headline", event.target.value)}
              />
            </Grid>
            <Grid container spacing={1}>
              <Grid size={4}>
                <TimeAutocomplete
                  label="Start Time"
                  time={diaryEntry.startTime ? dayjs(diaryEntry.startTime).format("HH:mm") : ''}
                  onTimeChange={(newTime) => handleOnChange("startTime", newTime)}
                />
              </Grid>
              <Grid size={4}>
                <TimeAutocomplete
                  label="End Time"
                  time={diaryEntry.endTime ? dayjs(diaryEntry.endTime).format("HH:mm") : ''}
                  onTimeChange={(newTime) => handleOnChange("endTime", newTime)}
                  options={endTimeOptions}
                  error={isRangeInverted}
                  helperText={
                    isRangeInverted ? "End time must be after start time" : ""
                  }
                />
              </Grid>
              <Grid container size={4} alignContent={"end"}>
                <Autocomplete
                  disableClearable
                  options={Object.keys(IssueTypeEnum).map(key => IssueTypeEnum[key as keyof typeof IssueTypeEnum])}
                  getOptionLabel={(option) => titleize(option)}
                  value={diaryEntry.issueType}
                  size="small"
                  fullWidth
                  renderInput={(autocompleteParams) =>
                    <TextField
                      {...autocompleteParams}
                      label="Type"
                      variant="outlined"
                      sx={{ p: 0, m: 0 }}
                    />
                  }
                  onChange={(_, value) => handleOnChange("issueType", value)}
                />
              </Grid>
            </Grid>
            <Grid>
              <TextEditor
                key={`selected-diary-entry-${selectedDiaryEntry.id === diaryEntry?.id}`}
                setText={(val: string) => {
                  const regex = /data-label="([^"]*)"/g;
                  const categoriesMentioned = uniq([...val.matchAll(regex)].map(match => match[1])) // ['Work Done', 'Quality'] etc...
                  const categoryIds = mapId(filter(issueCategories, category => categoriesMentioned.includes(category.name)));

                  handleOnChange("description", val);
                  setDiaryTags(categoryIds);
                }}
                text={diaryEntry.description || ""}
                options='IssueCategory'
                renderToolbar
                mentionChar="#"
              />

              <FormControlLabel
                control={<Checkbox checked={diaryEntry.flagged ?? false} onChange={(_, checked) => handleOnChange("flagged", checked)} />}
                label="Flag as a Potential Event"
              />
            </Grid>
            <Grid container spacing={2}>
              <Button
                variant="contained"
                onClick={handleUpdateEntry}
                disabled={disableButton}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedDiaryEntry(null)}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        )
      }
    </React.Fragment>
  );
};

export default DiarySingleEntry;
